import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, switchMap, tap, catchError, of } from 'rxjs';
import { BACKEND_URI } from "../config/config-vars";
import { SalesRep } from '../models/sales-rep.model';
import { Board } from '../models/board.model';

interface BoardStats {
  cashInDiff: number;
  cashOutDiff: number;
  coinInDiff: number;
  coinOutDiff: number;
  gamePlayedDiff: number;
  gameWonDiff: number;
  holdPercentage: number;
  profit: number;
  board: Board[];
  location: Location[];
  salesReps: SalesRep[];
}

const BASE_URL = `${BACKEND_URI}/api/boardStats`;

const GAME_NAME_MAPPINGS = [
  { keywords: ['RED', 'FUSION RED', 'FUSION RED SKILL', 'RED BLAZE'], name: 'RED' },  // Generalize the "RED" game to include other variations
  { keywords: ['GOLD'], name: 'GOLD LINK' },
  { keywords: ['KLASSIC', 'CLASSIC'], name: 'KLASSIC KRAZE' },
  { keywords: ['DIAMOND'], name: 'DIAMOND BLAST LINK' },
  { keywords: ['SILVER'], name: 'SILVER LINK' },
  { keywords: ['PLATINUM'], name: 'PLATINUM BLITZ' }
];

const STANDARD_GAME_NAMES = GAME_NAME_MAPPINGS.map(mapping => mapping.name);

@Injectable({
  providedIn: 'root'
})
export class BoardStatsReportService {
  constructor(private http: HttpClient) {}

  private hasNonZeroValues(stats: BoardStats): boolean {
    const epsilon = 0.001;
    const isEffectivelyZero = (n: number) => Math.abs(n) < epsilon;

    return !isEffectivelyZero(stats.cashInDiff) ||
           !isEffectivelyZero(stats.cashOutDiff) ||
           !isEffectivelyZero(stats.coinInDiff) ||
           !isEffectivelyZero(stats.coinOutDiff) ||
           stats.gamePlayedDiff > 0 ||
           stats.gameWonDiff > 0 ||
           !isEffectivelyZero(stats.holdPercentage) ||
           !isEffectivelyZero(stats.profit);
  }

  private standardizeGameName(name: string): string {
    if (!name) return '';
    
    const upperName = name.toUpperCase().trim();
  
    // Check if the name contains any "RED" variants and return "RED"
    const redKeywords = ['RED', 'FUSION RED', 'FUSION RED SKILL', 'RED BLAZE'];
    if (redKeywords.some(keyword => upperName.includes(keyword))) {
      return 'RED';  // Return a unified "RED GAME" name for all RED-related variations
    }
  
    // If it's already a standard name, return it as is
    if (STANDARD_GAME_NAMES.includes(upperName)) {
      return upperName;
    }
    
    // Look for keyword matches in the mapping
    for (const mapping of GAME_NAME_MAPPINGS) {
      if (mapping.keywords.some(keyword => upperName.includes(keyword))) {
        return mapping.name;
      }
    }
    
    // Default case: return the name as it is
    return upperName;
  }
  
  getBoardStatsReport(
    startDate: Date,
    endDate: Date,
    salesReps: SalesRep[],
    gameName?: string,
    boardAssetTag?: string,
    groupByBoard: boolean = false
  ): Observable<BoardStats[]> {
    // Format the dates just before passing them to the backend
    const startDateFormatted = this.formatDate(startDate);
    const endDateFormatted = this.formatDate(endDate);

    const params = this.buildRequestParams(
      startDateFormatted,  // Use the formatted startDate as string
      endDateFormatted,    // Use the formatted endDate as string
      salesReps,
      gameName,
      boardAssetTag,
      groupByBoard
    );
    
    return this.http.get<BoardStats[]>(`${BASE_URL}`, { params })
      .pipe(
        map(stats => stats.filter(stat => this.hasNonZeroValues(stat))),
        map(stats => stats.map(stat => {
          if (stat.board && stat.board.length > 0) {
            stat.board = stat.board.map(board => {
              if (board.installedGamePackage && board.installedGamePackage.name) {
                board.installedGamePackage.name = this.standardizeGameName(board.installedGamePackage.name);
              }
              return board;
            });
          }
          return stat;
        })),
        catchError(error => {
          console.error('Error fetching board stats:', error);
          return of([]);
        })
      );
  }

  exportToCsv(
    startDate: Date,  // Keep this as Date, not string
    endDate: Date,    // Keep this as Date, not string
    salesReps: SalesRep[],
    gameName?: string,
    boardAssetTag?: string,
    groupByBoard: boolean = false
  ): Observable<Blob | null> {
    // Format the startDate and endDate into the correct format for the backend
    const startDateFormatted = this.formatDate(startDate);  // Format to 'yyyy-MM-dd'T'HH:mm:ss'
    const endDateFormatted = this.formatDate(endDate);      // Format to 'yyyy-MM-dd'T'HH:mm:ss'

    console.log('Formatted startDate:', startDateFormatted);
    console.log('Formatted endDate:', endDateFormatted);

    const params = this.buildRequestParams(
      startDateFormatted,  // Use the formatted startDate as string
      endDateFormatted,    // Use the formatted endDate as string
      salesReps,
      gameName,
      boardAssetTag,
      groupByBoard
    );

    console.log('Export request params:', params.toString());

    return this.http.get(`${BASE_URL}/export`, {
      params,
      responseType: 'blob'
    }).pipe(
      tap(response => {
        console.log('Export response received:', response);
      }),
      catchError(error => {
        console.error('Error exporting to CSV:', error);
        return of(null);
      })
    );
  }

  private formatDate(date: Date): string {
    const pad = (num: number) => num.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  private buildRequestParams(
    startDate: string,  // Accept formatted string instead of Date
    endDate: string,    // Accept formatted string instead of Date
    salesReps: SalesRep[],
    gameName?: string,
    boardAssetTag?: string,
    groupByBoard: boolean = false
  ): HttpParams {
    let params = new HttpParams();
  
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
  
    if (salesReps && salesReps.length > 0) {
      const rep = salesReps[0];
      if (rep.firstName) {
        params = params.set('salesRepFirstName', rep.firstName.trim());
      }
      if (rep.lastName) {
        params = params.set('salesRepLastName', rep.lastName.trim());
      }
    }
  
    if (gameName && gameName.trim()) {
      const standardizedName = this.standardizeGameName(gameName.trim());
      if (STANDARD_GAME_NAMES.includes(standardizedName)) {
        params = params.set('gameName', standardizedName);
      }
    }
  
    if (boardAssetTag && boardAssetTag.trim()) {
      params = params.set('boardAssetTag', boardAssetTag.trim());
    }
  
    params = params.set('groupByBoard', groupByBoard.toString());
    params = params.set('excludeZeros', 'false');
  
    return params;
  }
  

  getSalesReps(): Observable<SalesRep[]> {
    return this.http.get<SalesRep[]>(`${BACKEND_URI}/api/sales-rep`);
  }
}
